/**
 * Element build animations (titles, images, histology viewers, labels...).
 *
 * Stored on each canvas element as:
 *   animation: { effect, trigger, delay, duration, order }
 *
 * trigger:
 *   "enter" – plays when the slide appears
 *   "click" – waits for the presenter to advance
 *   "with"  – starts together with the previous animation
 *   "after" – starts when the previous animation ends
 */

export const ELEMENT_EFFECTS = [
  { id: "fadeIn", label: "Fade In" },
  { id: "riseUp", label: "Rise Up" },
  { id: "zoomIn", label: "Zoom In" },
  { id: "scale", label: "Scale" },
  { id: "slideIn", label: "Slide In" },
  { id: "blurIn", label: "Blur In" },
  { id: "staggerText", label: "Staggered Text Reveal" },
  { id: "float", label: "Gentle Floating" },
  { id: "spotlight", label: "Spotlight Focus" },
];

export const ELEMENT_TRIGGERS = [
  { id: "enter", label: "On slide enter" },
  { id: "click", label: "On click" },
  { id: "after", label: "After previous" },
  { id: "with", label: "With previous" },
];

// Restrained academic defaults: short, soft, no overshoot.
export const DEFAULT_ELEMENT_ANIMATION = Object.freeze({
  effect: "riseUp",
  trigger: "click",
  delay: 0,
  duration: 600,
});

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

export const effectKeyframes = (effect) => {
  switch (effect) {
    case "riseUp":
      return [
        { opacity: 0, transform: "translateY(28px)" },
        { opacity: 1, transform: "translateY(0)" },
      ];
    case "zoomIn":
      return [
        { opacity: 0, transform: "scale(0.82)" },
        { opacity: 1, transform: "scale(1)" },
      ];
    case "scale":
      return [
        { opacity: 0, transform: "scale(1.12)" },
        { opacity: 1, transform: "scale(1)" },
      ];
    case "slideIn":
      return [
        { opacity: 0, transform: "translateX(-56px)" },
        { opacity: 1, transform: "translateX(0)" },
      ];
    case "blurIn":
      return [
        { opacity: 0, filter: "blur(12px)" },
        { opacity: 1, filter: "blur(0px)" },
      ];
    case "spotlight":
      return [
        { opacity: 0, transform: "scale(0.96)", filter: "brightness(1.6) drop-shadow(0 0 0 rgba(56,189,248,0))" },
        { opacity: 1, transform: "scale(1.02)", filter: "brightness(1.15) drop-shadow(0 0 28px rgba(56,189,248,0.55))", offset: 0.6 },
        { opacity: 1, transform: "scale(1)", filter: "brightness(1) drop-shadow(0 0 0 rgba(56,189,248,0))" },
      ];
    case "staggerText":
    case "float":
    case "fadeIn":
    default:
      return [{ opacity: 0 }, { opacity: 1 }];
  }
};

const sortByOrder = (items) =>
  items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const ao = Number.isFinite(a.item.animation?.order) ? a.item.animation.order : a.index;
      const bo = Number.isFinite(b.item.animation?.order) ? b.item.animation.order : b.index;
      return ao - bo || a.index - b.index;
    })
    .map(({ item }) => item);

/** Elements that have a build animation, in timeline order. */
export const getAnimatedElements = (elements) =>
  sortByOrder(
    (Array.isArray(elements) ? elements : []).filter(
      (el) => el && el.id && !el.isBackground && el.animation && el.animation.effect
    )
  );

/**
 * Turn element animations into a schedule.
 * Returns { schedule: Map<id, {step,start,delay,duration,effect}>, steps }.
 * `steps` is the number of presenter clicks needed to reveal everything.
 */
export const buildAnimationSchedule = (elements) => {
  const schedule = new Map();
  let step = 0;
  let prevStart = 0;
  let prevEnd = 0;
  let seenAny = false;

  getAnimatedElements(elements).forEach((el) => {
    const a = el.animation;
    const duration = Math.max(100, Number(a.duration) || DEFAULT_ELEMENT_ANIMATION.duration);
    const delay = Math.max(0, Number(a.delay) || 0);
    let start = 0;
    const trigger = a.trigger || "click";

    if (trigger === "click") {
      step += 1;
      start = 0;
    } else if (trigger === "enter") {
      // Entrance items always belong to the slide's first beat.
      schedule.set(el.id, { step: 0, start: 0, delay, duration, effect: a.effect });
      return;
    } else if (trigger === "with" && seenAny) {
      start = prevStart;
    } else if (trigger === "after" && seenAny) {
      start = prevEnd;
    }

    schedule.set(el.id, { step, start, delay, duration, effect: a.effect });
    prevStart = start + delay;
    prevEnd = start + delay + duration;
    seenAny = true;
  });

  return { schedule, steps: step };
};

const canAnimate = (node) => Boolean(node && typeof node.animate === "function");

/**
 * Play one element's entrance on a DOM node. Returns a cancel function.
 * Staggered text animates each paragraph/line of a text box in turn;
 * Gentle Floating keeps a slow bob going after the entrance.
 */
export const playElementEntrance = (node, entry, extraDelay = 0) => {
  if (!canAnimate(node) || !entry) return () => {};
  const timing = {
    duration: entry.duration,
    delay: extraDelay + entry.start + entry.delay,
    easing: EASE,
    fill: "backwards",
  };
  const running = [];

  if (entry.effect === "staggerText") {
    const lines = node.querySelectorAll(".ql-editor > *");
    if (lines.length > 1) {
      const per = Math.min(160, Math.max(70, entry.duration / lines.length));
      lines.forEach((line, i) => {
        if (!canAnimate(line)) return;
        running.push(
          line.animate(
            [
              { opacity: 0, transform: "translateY(14px)" },
              { opacity: 1, transform: "translateY(0)" },
            ],
            { ...timing, delay: timing.delay + i * per }
          )
        );
      });
    } else {
      running.push(node.animate(effectKeyframes("riseUp"), timing));
    }
  } else {
    running.push(node.animate(effectKeyframes(entry.effect), timing));
  }

  if (entry.effect === "float") {
    running.push(
      node.animate(
        [
          { translate: "0 0" },
          { translate: "0 -7px" },
          { translate: "0 0" },
        ],
        {
          duration: 4200,
          delay: timing.delay + entry.duration,
          iterations: Infinity,
          easing: "ease-in-out",
        }
      )
    );
  }

  return () => {
    running.forEach((a) => {
      try {
        a.cancel();
      } catch (error) {
        /* ignore */
      }
    });
  };
};

/**
 * Editor preview: play every animation on the live canvas in order.
 * Clicks are simulated with a short pause so the professor can see the build.
 */
export const previewAnimationsOnCanvas = (rootEl, elements) => {
  if (!rootEl) return () => {};
  const { schedule } = buildAnimationSchedule(elements);
  const CLICK_GAP = 450;
  const stepOffsets = new Map();
  let cursor = 0;
  const byStep = new Map();
  schedule.forEach((entry, id) => {
    if (!byStep.has(entry.step)) byStep.set(entry.step, []);
    byStep.get(entry.step).push({ id, entry });
  });
  [...byStep.keys()]
    .sort((a, b) => a - b)
    .forEach((step) => {
      stepOffsets.set(step, cursor);
      const end = byStep
        .get(step)
        .reduce((max, { entry }) => Math.max(max, entry.start + entry.delay + entry.duration), 0);
      cursor += end + CLICK_GAP;
    });

  const cancels = [];
  schedule.forEach((entry, id) => {
    const node = rootEl.querySelector(`[data-el-id="${CSS.escape(id)}"] .item-container`);
    if (!node) return;
    cancels.push(playElementEntrance(node, { ...entry, effect: entry.effect === "float" ? "fadeIn" : entry.effect }, stepOffsets.get(entry.step) || 0));
  });
  return () => cancels.forEach((fn) => fn());
};

/** Number of click steps an element's guided focus contributes. */
export const focusStepCount = (el) => {
  if (!el) return 0;
  if (el.type === "SLIDE") {
    const stops = Array.isArray(el.slideFocus) ? el.slideFocus.length : 0;
    const labels =
      el.annotationReveal === "progressive" && Array.isArray(el.slideAnnotations?.features)
        ? el.slideAnnotations.features.length
        : 0;
    return stops + labels;
  }
  if (el.type === "3D_MODEL") return Array.isArray(el.cameraViews) ? el.cameraViews.length : 0;
  return 0;
};

/**
 * Full presentation plan for one slide: element builds first, then each
 * histology / anatomy element's guided focus steps in layer order.
 */
export const buildPlaybackPlan = (elements) => {
  const { schedule, steps } = buildAnimationSchedule(elements);
  const focusStart = new Map();
  let cursor = steps;
  (Array.isArray(elements) ? elements : []).forEach((el) => {
    const count = focusStepCount(el);
    if (!count || !el.id) return;
    focusStart.set(el.id, cursor + 1);
    cursor += count;
  });
  return { schedule, focusStart, steps: cursor };
};
